import { Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { SpriDrawerComponent } from '@spri/components/drawer';
import { SpriLoadingBarComponent } from '@spri/components/loading-bar';
import { TenantContextService } from 'app/core/services/tenant-context.util';
import { TenantBrandingStore } from 'app/core/tenant-branding';
import { ManualFormContentComponent } from 'app/modules/public/patient-form-page/manual-form-content/manual-form-content.component';
import {
  isClinicAccess,
  isClinicIpAllowed,
  isIpadDevice,
} from 'app/modules/public/patient-form-page/patient-form-access.util';
import { environment } from 'environments/environment';

const isDevelopment = !environment.production;

@Component({
  selector: 'spri-patient-form-layout',
  templateUrl: './patient-form-layout.component.html',
  styleUrls: ['./patient-form-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [ManualFormContentComponent, SpriLoadingBarComponent, SpriDrawerComponent, RouterOutlet, MatIconModule],
})
export class PatientFormLayoutComponent {
  private readonly route = inject(ActivatedRoute);
  protected readonly tenantContextService = inject(TenantContextService);
  private readonly tenantBrandingStore = inject(TenantBrandingStore);

  // Configure layout imagery here
  readonly defaultFooterTitle = 'Steadman Philippon Research Institute';
  private readonly defaultRightImageUrl: string | null = '/images/patient-form/tsc-hall.jpg';
  private readonly defaultFooterLogos: { src: string; alt: string }[] = [
    { src: '/images/patient-form/us-coalition-logo.png', alt: 'Steadman Philippon Research Institute' },
    { src: '/images/patient-form/usop-nmc.png', alt: 'Steadman Philippon Research Institute' },
  ];
  readonly clinicIpAllowlist = environment.clinicIpAllowlist;

  readonly logoUrl = computed(() => this.tenantContextService.logoUrl() ?? '/images/logo/steadman-logo.svg');

  readonly footerTitle = computed(
    () => this.tenantContextService.tenantTitle() ?? this.tenantContextService.displayName() ?? this.defaultFooterTitle
  );

  readonly rightImageUrl = computed(
    () => this.tenantBrandingStore.branding()?.publicRightImageUrl ?? this.defaultRightImageUrl
  );

  readonly footerLogos = computed(() => {
    const logos = this.tenantBrandingStore.branding()?.publicFooterLogos;
    if (logos?.length) {
      return logos.map((logo) => ({
        src: logo.src,
        alt: logo.alt ?? this.footerTitle(),
      }));
    }
    return this.defaultFooterLogos;
  });

  readonly isClinicAccess = computed(() => isClinicAccess(this.route.snapshot.queryParamMap));

  readonly isIpadDevice = computed(() => isIpadDevice());

  readonly isClinicIpAllowed = computed(() => {
    if (!this.isClinicAccess()) {
      return true;
    }
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    return isClinicIpAllowed(this.clinicIpAllowlist, hostname);
  });

  readonly showManualFormLink = computed(() => {
    const clinicOk = this.isClinicAccess() && this.isClinicIpAllowed();
    const deviceOk = this.isIpadDevice() || isDevelopment;
    return clinicOk && deviceOk;
  });

  /** Whether the manual form offcanvas is open */
  manualOffcanvasOpen = false;

  onManualFormClick(): void {
    this.manualOffcanvasOpen = true;
  }

  closeManualFormOffcanvas(): void {
    this.manualOffcanvasOpen = false;
  }
}
